import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SparklesIcon, ArrowUpIcon, CarIcon, CalendarIcon, PlaneIcon, ShieldCheckIcon } from 'lucide-react';
import { useBusiness } from '../components/BusinessContext';
type ChatMessage = {
  role: 'user' | 'ai';
  content: string;
};
// Rental-specific quick prompts — NOT generic marketplace categories.
const SHOP_PROMPTS = [{
  text: 'Show me your available SUVs',
  icon: CarIcon
}, {
  text: 'What are your monthly rental rates?',
  icon: CalendarIcon
}, {
  text: 'Do you offer airport pickup & delivery?',
  icon: PlaneIcon
}, {
  text: 'I need a car with full insurance',
  icon: ShieldCheckIcon
}];
export function Shop() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    hub
  } = useBusiness();
  const [query, setQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const isCurrentShop = hub?.shopSlug === slug;
  const shopName = isCurrentShop ? hub?.name ?? 'Easylease' : slug || 'Shop';
  const shopLogo = isCurrentShop ? hub?.logo : null;
  const shopHandle = hub?.shopSlug ?? slug;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [chat, isTyping]);
  const ask = (text: string) => {
    if (!text.trim()) return;
    setChat(prev => [...prev, {
      role: 'user',
      content: text
    }]);
    setQuery('');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setChat(prev => [...prev, {
        role: 'ai',
        content: `Great choice! At ${shopName} we offer daily, weekly, and monthly car rentals across the UAE — from economy and sedans to premium SUVs and luxury vehicles, all with full insurance options, airport pickup, and free delivery in Dubai. Tell me your dates and budget and I'll pull together the best options for you.`
      }]);
    }, 1100);
  };
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    ask(query);
  };
  return <div className="flex-1 flex flex-col h-full bg-white relative overflow-hidden" data-id="element-3123">
      {/* Background ambient glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" data-id="element-3124">
        <div className="absolute top-[18%] left-1/2 -translate-x-1/2 w-[900px] h-[640px] bg-ai-purple/[0.06] blur-[140px] rounded-full" data-id="element-3125" />
        <div className="absolute top-[8%] left-[12%] w-[420px] h-[420px] bg-primary-indigo/[0.05] blur-[130px] rounded-full" data-id="element-3126" />
        <div className="absolute bottom-[6%] right-[14%] w-[460px] h-[460px] bg-electric-cyan/[0.05] blur-[140px] rounded-full" data-id="element-3127" />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-24 relative z-10" data-id="element-3128">
        <div className="max-w-3xl mx-auto w-full h-full flex flex-col" data-id="element-3129">
          {chat.length === 0 ?
        // Hero (mirrors Discover, tailored to the shop)
        <div className="flex-1 flex flex-col items-center justify-center py-6" data-id="element-3130">
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="w-full flex flex-col items-center relative z-10" data-id="element-3131">
                {/* Shop logo with rotating ring */}
                <div className="relative mb-5" data-id="element-3132">
                  <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 20,
                repeat: Infinity,
                ease: 'linear'
              }} className="absolute -inset-3.5 border border-slate-200 border-dashed rounded-full opacity-50" data-id="element-3133" />
                  <div className="w-20 h-20 lg:w-24 lg:h-24 bg-white rounded-full flex items-center justify-center shadow-sm border border-slate-100 relative z-10 overflow-hidden" data-id="element-3134">
                    {shopLogo ? <img src={shopLogo} alt={shopName} className="w-full h-full object-cover" data-id="element-3135" /> : <span className="text-2xl font-bold text-primary-indigo" data-id="element-3136">
                        {shopName.charAt(0)}
                      </span>}
                  </div>
                </div>

                {/* Heading */}
                <div className="text-center space-y-2.5 mb-6" data-id="element-3137">
                  <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-dark-navy tracking-tight" data-id="element-3138">
                    Welcome to{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-indigo to-ai-purple" data-id="element-3139">
                      {shopName}
                    </span>
                  </h1>
                  <p className="text-slate-500 text-sm lg:text-base max-w-xl mx-auto leading-relaxed" data-id="element-3140">
                    Your trusted UAE car rental partner — economy, sedans,
                    premium SUVs and luxury cars on daily, weekly and monthly
                    plans. Full insurance, airport pickup, and free Dubai
                    delivery.
                  </p>
                  {shopHandle && <p className="text-xs font-medium text-primary-indigo" data-id="element-3141">
                      talab.app/{shopHandle}
                    </p>}
                </div>

                {/* AI Input */}
                <div className="w-full max-w-3xl bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 p-2 mb-6 relative group transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] z-10" data-id="element-3142">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-indigo/20 to-ai-purple/20 rounded-3xl blur opacity-0 group-focus-within:opacity-100 transition duration-500" data-id="element-3143" />
                  <form onSubmit={handleSubmit} className="relative bg-white rounded-[22px] p-5 flex flex-col gap-3" data-id="element-3144">
                    <textarea ref={textareaRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }} placeholder={`Ask ${shopName} about our cars, rates or delivery…`} className="w-full resize-none outline-none text-base lg:text-lg text-slate-800 placeholder:text-slate-300 bg-transparent min-h-[44px]" rows={1} data-id="element-3145" />
                    <div className="flex items-center justify-between" data-id="element-3146">
                      <span className="text-xs text-slate-400 flex items-center gap-1.5" data-id="element-3147">
                        <SparklesIcon className="w-3.5 h-3.5 text-ai-purple" data-id="element-3148" />
                        Powered by {shopName} AI
                      </span>
                      <button type="submit" disabled={!query.trim()} className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-indigo to-ai-purple text-white flex items-center justify-center shadow-sm hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all" aria-label="Send" data-id="element-3149">
                        <ArrowUpIcon className="w-5 h-5" data-id="element-3150" />
                      </button>
                    </div>
                  </form>
                </div>

                {/* Tailored prompts (services, not generic categories) */}
                <div className="w-full max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-id="element-3151">
                  {SHOP_PROMPTS.map(p => {
                const Icon = p.icon;
                return <button key={p.text} onClick={() => ask(p.text)} className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-100 rounded-2xl text-left text-sm text-slate-700 shadow-sm hover:border-primary-indigo/40 hover:text-primary-indigo transition-colors" data-id="element-3152">
                        <Icon className="w-4 h-4 text-primary-indigo flex-shrink-0" data-id="element-3153" />
                        <span className="truncate" data-id="element-3154">{p.text}</span>
                      </button>;
              })}
                </div>
              </motion.div>
            </div> :
        // Chat thread
        <div className="flex-1 py-8 space-y-5" data-id="element-3155">
              <button onClick={() => setChat([])} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 shadow-sm hover:border-primary-indigo hover:text-primary-indigo transition-colors mb-2" data-id="element-3156">
                <SparklesIcon className="w-4 h-4" data-id="element-3157" /> New conversation
              </button>

              {chat.map((m, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-3158">
                  {m.role === 'ai' && <div className="w-9 h-9 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center mr-3 flex-shrink-0 overflow-hidden" data-id="element-3159">
                      {shopLogo ? <img src={shopLogo} alt={shopName} className="w-full h-full object-cover" data-id="element-3160" /> : <SparklesIcon className="w-4 h-4 text-primary-indigo" data-id="element-3161" />}
                    </div>}
                  <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${m.role === 'user' ? 'bg-primary-indigo text-white rounded-br-md' : 'bg-white border border-slate-100 text-slate-700 shadow-sm rounded-bl-md'}`} data-id="element-3162">
                    {m.content}
                  </div>
                </motion.div>)}

              {isTyping && <div className="flex justify-start" data-id="element-3163">
                  <div className="w-9 h-9 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center mr-3 overflow-hidden" data-id="element-3164">
                    {shopLogo ? <img src={shopLogo} alt={shopName} className="w-full h-full object-cover" data-id="element-3165" /> : <SparklesIcon className="w-4 h-4 text-primary-indigo" data-id="element-3166" />}
                  </div>
                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1" data-id="element-3167">
                    {[0, 1, 2].map(d => <motion.span key={d} className="w-1.5 h-1.5 bg-slate-300 rounded-full" animate={{
                opacity: [0.3, 1, 0.3]
              }} transition={{
                duration: 1,
                repeat: Infinity,
                delay: d * 0.2
              }} data-id="element-3168" />)}
                  </div>
                </div>}
              <div ref={bottomRef} data-id="element-3169" />
            </div>}
        </div>
      </div>

      {/* Persistent input when in a conversation */}
      {chat.length > 0 && <div className="flex-shrink-0 px-4 pb-7 pt-2 relative z-20 bg-gradient-to-t from-white via-white to-transparent" data-id="element-3170">
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto w-full bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 p-2 flex items-end gap-2" data-id="element-3171">
            <textarea value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
        }} placeholder={`Message ${shopName}…`} className="flex-1 resize-none outline-none text-base text-slate-800 placeholder:text-slate-300 bg-transparent px-3 py-2.5 min-h-[44px]" rows={1} data-id="element-3172" />
            <button type="submit" disabled={!query.trim()} className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-indigo to-ai-purple text-white flex items-center justify-center shadow-sm hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex-shrink-0" aria-label="Send" data-id="element-3173">
              <ArrowUpIcon className="w-5 h-5" data-id="element-3174" />
            </button>
          </form>
        </div>}
    </div>;
}